import { useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ContactsProvider,
  useContacts,
} from "../../contexts/ContactsContext";
import ContactsSidebar from "../../components/ContactsSidebar";
import { useChatHeader } from "../../../../contexts/ChatHeaderContext";
import { getShopsRequest } from "../../../Shop/slices/shopActions";
import pipelineStageApi from "../../../../api/pipelineStageApi";
import tagApi from "../../../../api/tagApi";
import userApi from "../../../../api/userApi";
import shopGroupApi from "../../../../api/shopGroupApi";

/**
 * Inner body — cần context `ContactsProvider` đã mount trước khi gọi.
 */
const ContactsBody = () => {
  const { selectedShopIds, setSelectedShopIds, activeGroup, setActiveGroup, setLookups } =
    useContacts();
  const shops = useSelector((s) => s.shops?.data || []);
  const shopsLoading = useSelector((s) => s.shops?.isLoading);
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const { setHeaderData, clearHeaderData } = useChatHeader();
  const [shopGroups, setShopGroups] = useState([]);

  // Fetch shops nếu chưa có (mở /contacts trong tab mới)
  useEffect(() => {
    const accountId = auth.user?.accountId;
    const staffId = auth.user?._id;
    if (
      accountId &&
      staffId &&
      !shopsLoading &&
      (!shops || shops.length === 0)
    ) {
      dispatch(getShopsRequest({ accountId, staffId }));
    }
  }, [dispatch, auth.user?.accountId, auth.user?._id, shops?.length, shopsLoading]);

  // Tính shop hiển thị trên ShopSwitcher trigger button
  const selectedShop = useMemo(() => {
    if (!shops?.length) return null;
    if (selectedShopIds.length === 1) {
      return shops.find((s) => s._id === selectedShopIds[0]) || null;
    }
    return null;
  }, [shops, selectedShopIds]);

  // Load shopGroups + staffs + tags + pipelineStages một lần
  useEffect(() => {
    const accountId = auth.user?.accountId;
    const staffId = auth.user?._id;
    if (!accountId || !staffId) return;

    let canceled = false;

    // Unwrap response: BE trả { status, data: [...] } cho các endpoint này,
    // nhưng tagZalos/shopGroups có thể trả array trực tiếp. Hàm helper xử lý cả 2.
    const unwrap = (resp) => {
      if (Array.isArray(resp)) return resp;
      if (resp && Array.isArray(resp.data)) return resp.data;
      return [];
    };

    (async () => {
      try {
        const [stagesRes, tagsRes, zaloTagsRes, staffsRes, groupsRes] =
          await Promise.all([
            pipelineStageApi.getByAccountId(accountId).catch(() => null),
            tagApi.getByAccountId(accountId).catch(() => null),
            tagApi.getZaloTagsByAccountId(accountId).catch(() => null),
            userApi.getByAccountId(accountId).catch(() => null),
            shopGroupApi.getAll(staffId).catch(() => null),
          ]);

        if (canceled) return;

        const stages = unwrap(stagesRes);
        const tags = unwrap(tagsRes);
        const zaloTags = unwrap(zaloTagsRes);
        const staffs = unwrap(staffsRes);
        const groups = unwrap(groupsRes);

        setShopGroups(groups);

        const stagesById = {};
        for (const s of stages) {
          const id = s._id || s.id;
          if (id) stagesById[id] = { ...s, id };
        }
        const tagsById = {};
        for (const t of tags) {
          const id = t._id || t.id;
          if (id) tagsById[id] = { ...t, id };
        }
        const tagZalosById = {};
        for (const t of zaloTags) {
          const id = t._id || t.id;
          if (id) tagZalosById[id] = { ...t, id };
        }
        const staffsById = {};
        for (const s of staffs) {
          const id = s._id || s.id;
          if (id) staffsById[id] = { ...s, id };
        }

        setLookups({
          pipelineStagesById: stagesById,
          tagsById,
          tagZalosById,
          staffsById,
          shopGroups: groups,
        });
      } catch (e) {
        console.error("[ContactsLayout] load lookups error:", e);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [auth.user?.accountId, auth.user?._id, setLookups]);

  // Wire header (ShopSwitcher) vào TopNavBar qua ChatHeaderContext
  useEffect(() => {
    if (!auth.user) return;
    setHeaderData({
      shops,
      shopGroups,
      activeGroup,
      currentUser: selectedShop || { name: "Chọn Zalo" },
      selectedShopIds,
      hideNavItems: true,
      singleSelect: true,
      changeShop: (shop) => {
        setActiveGroup(null);
        setSelectedShopIds([shop._id]);
      },
      onApplyMultiSelect: (ids) => {
        setActiveGroup(null);
        setSelectedShopIds(ids);
      },
      onChangeGroup: (group) => {
        setActiveGroup(group);
        if (Array.isArray(group?.shopIds)) {
          setSelectedShopIds(group.shopIds);
        }
      },
      onLogoutClick: undefined,
      onManageStaffClick: () => {},
      onClickPermission: () => {},
      onClickSetting: () => {},
      onClickHiddenConversations: () => {},
      showManageStaffButton: false,
      showPermissionButton: false,
      showStagesManagerButton: false,
    });
    return () => clearHeaderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops, selectedShopIds, activeGroup, auth.user, selectedShop, shopGroups]);

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <ContactsSidebar />
      <main className="flex-1 overflow-hidden flex flex-col bg-white">
        <Outlet />
      </main>
    </div>
  );
};

const ContactsLayout = () => {
  return (
    <ContactsProvider>
      <ContactsBody />
    </ContactsProvider>
  );
};

export default ContactsLayout;
